import { BALANCE, FACTIONS, type AgreementId } from '@sd/content';
import {
  atWar,
  hasAgreement,
  pairIs,
  type Command,
  type GameState,
  type PlayerState,
  type Proposal,
} from '@sd/sim';
import { strength } from './fleets';
import type { Bot } from './match';

/**
 * Diplomacy of the bots (#74, author's decision 20.10.2026): they answer
 * offers by their own benefit, as a person would, and make offers of their
 * own — peace when a war goes badly, a trade agreement to a contract
 * partner, a pact with a stronger neighbour, an alliance against a shared
 * enemy. Bot strategy, not game rules: thresholds live here.
 */
export interface DiplomatStyle {
  /** Holds out longer in a war, refuses pacts and trade with the rival it means to attack. */
  readonly warlike: boolean;
  /** Seeks trade agreements with its contract partners. */
  readonly trader: boolean;
  /** The rival a warlike bot means to attack, if any. */
  readonly target?: (state: GameState, self: PlayerState) => PlayerState | null;
}

/** Reputation a bot wants from a partner before trading with it, and before an alliance. */
const TRUST = { trade: 40, alliance: 50 } as const;
/** A war is going badly when the enemy's fleet is this many times stronger. */
const LOSING = { calm: 1.25, warlike: 2 } as const;
/** ...or, for a calm bot, when it has dragged on this many cycles; a warlike one fights on. */
const WEARY = { calm: 10, warlike: Infinity } as const;
/** A neighbour this many times stronger is worth a pact. */
const MENACE = 1.5;

const cycles = (ticks: number) => ticks / BALANCE.cycleTicks;
/** How often a bot looks at its diplomacy. */
const THINK_TICKS = 50;
/** Seats think on different ticks. */
const seat = (state: GameState, playerId: string) =>
  (state.players.findIndex((p) => p.id === playerId) * 7) % THINK_TICKS;

export function makeDiplomat(style: DiplomatStyle): Bot {
  return (state, playerId) => {
    // Every five seconds on a schedule, not by the dice: diplomacy leaves the shared
    // random stream of the other bots untouched, so only its decisions change a match.
    if (state.pause || state.tick % THINK_TICKS !== seat(state, playerId)) return [];
    const self = state.players.find((p) => p.id === playerId);
    if (!self) return [];
    const mine = strength(state, playerId);
    const target = style.warlike ? (style.target?.(state, self) ?? null) : null;
    const mood = style.warlike ? 'warlike' : 'calm';

    /** Whether the war with `other` is one this bot would rather end. */
    const wantsPeace = (other: PlayerState) => {
      const war = state.wars.find((w) => pairIs(w, playerId, other.id));
      if (!war) return false;
      // A war against a victory countdown is the answer to it: no peace until it is over.
      if (state.countdowns.some((c) => c.playerId === other.id)) return false;
      const theirs = strength(state, other.id);
      const losing = theirs > 0 && theirs >= mine * LOSING[mood];
      const weary = war.since !== undefined && cycles(state.tick - war.since) >= WEARY[mood];
      const crisis = state.crises.some((c) => c.warned.includes(playerId));
      // Under the fog (#81) the enemy's fleet may be out of sight, but its raids and landings are not.
      const hurt =
        state.raids.some((r) => r.victim === playerId && r.owner === other.id) ||
        state.sieges.some((s) => s.victim === playerId && s.owner === other.id);
      return losing || weary || (hurt && !style.warlike) || (crisis && !style.warlike);
    };
    const shareEnemy = (other: string) =>
      state.wars.some(
        (w) =>
          (w.a === playerId || w.b === playerId) &&
          [w.a, w.b].some((x) => x !== playerId && atWar(state, other, x)),
      );
    /** Whether an agreement of `kind` with `other` is worth it to this bot. */
    const worth = (kind: AgreementId | 'peace', other: PlayerState): boolean => {
      if (kind === 'peace') return wantsPeace(other);
      if (atWar(state, playerId, other.id)) return false;
      if (target?.id === other.id) return false;
      switch (kind) {
        case 'nonAggression':
          // A pact costs a calm bot nothing; a warlike one signs only with someone it fears.
          return !style.warlike || strength(state, other.id) >= mine * MENACE;
        case 'trade':
          // Worth it when the partner's cheap good is one this bot has to buy.
          return (
            other.reputation >= TRUST.trade &&
            FACTIONS[other.faction].cheapGood !== FACTIONS[self.faction].cheapGood
          );
        case 'alliance':
          return other.reputation >= TRUST.alliance && shareEnemy(other.id);
      }
    };

    // 1. Answer offers made to this bot.
    const offers = state.proposals.filter((p: Proposal) => p.to === playerId);
    for (const offer of offers) {
      const other = state.players.find((p) => p.id === offer.from);
      if (other && worth(offer.kind, other)) {
        return [{ type: 'accept', playerId, other: other.id, kind: offer.kind }];
      }
    }

    // 2. Make offers of its own, one at a time.
    const proposed = (other: string, kind: AgreementId | 'peace') =>
      state.proposals.some((p) => p.from === playerId && p.to === other && p.kind === kind);
    const propose = (other: string, kind: AgreementId | 'peace'): Command[] =>
      proposed(other, kind) ? [] : [{ type: 'propose', playerId, other, kind }];
    const rivals = state.players.filter((p) => p.id !== playerId);
    for (const other of rivals) {
      if (wantsPeace(other)) {
        const offer = propose(other.id, 'peace');
        if (offer.length) return offer;
      }
    }
    if (style.trader) {
      for (const c of state.contracts) {
        if (c.status !== 'active' || (c.seller !== playerId && c.buyer !== playerId)) continue;
        const other = rivals.find((p) => p.id === (c.seller === playerId ? c.buyer : c.seller));
        if (!other || hasAgreement(state.agreements, playerId, other.id, 'trade')) continue;
        if (!worth('trade', other)) continue;
        const offer = propose(other.id, 'trade');
        if (offer.length) return offer;
      }
    }
    if (!style.warlike) {
      for (const other of rivals) {
        if (atWar(state, playerId, other.id)) continue;
        const pact = hasAgreement(state.agreements, playerId, other.id, 'nonAggression');
        if (!pact && strength(state, other.id) >= Math.max(1, mine) * MENACE) {
          const offer = propose(other.id, 'nonAggression');
          if (offer.length) return offer;
        }
        const allied = hasAgreement(state.agreements, playerId, other.id, 'alliance');
        if (!allied && worth('alliance', other)) {
          const offer = propose(other.id, 'alliance');
          if (offer.length) return offer;
        }
      }
    }
    return [];
  };
}
